import React, {
    useEffect, useState, useContext, useCallback
} from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import _ from 'lodash';
import { Loader, useModal } from '@jutro/components';
import { Wizard } from '@xengage/gw-portals-wizard-react';
import { ViewModelServiceContext, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/prefer-default-export
import { useAuthentication } from '@xengage/gw-digital-auth-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import 'gw-components-platform-react/platform-styles.scss';

import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { PolicyChange, messages as commonMessages } from 'gw-capability-policychange-common-react';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import messages from './CEPAPolicyChangeWizard.messages';
import wizardConfig from './config/pa-policychange-wizard-config.json5';
import styles from './CEPAPolicyChangeWizard.module.scss';

function CEPAPolicyChangeWizard(props) {
    const modalApi = useModal();
    const { steps, title } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const viewModelService = useContext(ViewModelServiceContext);
    const { location, history } = props;
    const { authHeader } = useAuthentication();
    const { EndorsementService } = useDependencies('EndorsementService');

    const onInit = useCallback(() => {
        const policyNumber = _.get(location, 'state.policyNumber', null);
        const errorModalBox = (errorMessage) => {
            modalApi.showConfirm(errorMessage).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                setIsLoading(false);
                return history.push(`/contactAgent/${policyNumber}`);
            }, _.noop);
        };
        setIsLoading(true);
        EndorsementService.loadEndorsement(policyNumber, authHeader)
            .then((responseData) => {
                const viewModelContext = {
                    isCustomerEngage: true
                };
                const submission = viewModelService.create(
                    new PolicyChange(responseData),
                    'pc',
                    'edge.capabilities.policychange.dto.PolicyChangeDataDTO',
                    viewModelContext
                );
                setInitialSubmission(submission);
            })
            .catch(() => {
                errorModalBox({
                    title: commonMessages.unableToLoadDraftPolicy,
                    message: commonMessages.somethingWentWrong,
                    status: 'warning',
                    icon: 'mi-error-outline'
                });
            })
            .catch(() => {
                errorModalBox({
                    title: commonMessages.unableToStartPolicy,
                    message: commonMessages.anErrorOccurred,
                    status: 'warning',
                    icon: 'mi-error-outline'
                });
            }).finally(() => {
                setIsLoading(false);
            });
    }, [EndorsementService, authHeader, history, location, modalApi, viewModelService]);

    useEffect(
        () => {
            if (!location.state) {
                history.push('/');
                return;
            }
            const {
                state: { policyNumber }
            } = location;
            if (!policyNumber) {
                return;
            }

            onInit();
        },
        // Disabled so we don't rerun this function on every rerender
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleOnCancel = useCallback(({ wizardData, wizardSnapshot, param: nextPath = '' }) => {
        const { submissionVM } = wizardData;
        const status = _.get(submissionVM.value, 'status');
        const jobID = _.get(submissionVM.value, 'jobID');
        const policyNumber = _.get(submissionVM.value, 'policyNumber');
        if ((status === 'Bound' && !_.isNil(jobID)) || (nextPath.startsWith('/change') || nextPath.startsWith('/contactAgent')) || hasErrorOccurred) {
            return true;
        }
        return modalApi.showConfirm({
            title: platformMessages.wantToCancel,
            message: messages.infoWillBeSavedAsDraft,
            status: 'warning',
            icon: 'gw-error-outline'
        }).then((results) => {
            if (results === 'cancel' || results === 'close') {
                return _.noop();
            }
            setIsLoading(true);
            const isValid = submissionVM.aspects.subtreeValid && submissionVM.aspects.valid;
            const payload = isValid ? submissionVM.value : _.get(wizardSnapshot, 'submissionVM.value');
            payload.beforeSave();
            const quoteStatus = _.get(wizardSnapshot, 'baseData.periodStatus.value.code')
                || _.get(submissionVM.value, 'status');
            const isStatusQuoted = quoteStatus !== 'Quoted';
            let redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/change/${jobID}/summary`;
            if (!jobID) {
                redirectLobPath = `/policies/${policyNumber}/summary`;
            }
            let exitPromise;
            if (appConfig.persona === 'policyholder') {
                redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/account-policy-details/${policyNumber}`;
            }
            if (appConfig.persona === 'policyholder') {
                if (quoteStatus !== 'Bound') {
                    exitPromise = EndorsementService.withdrawEndorsement([jobID], authHeader);
                } else {
                    return history.push(redirectLobPath);
                }
            } else if (isStatusQuoted) {
                exitPromise = EndorsementService.saveEndorsement([payload], authHeader);
            } else {
                return history.push(redirectLobPath);
            }
            exitPromise.then(() => {
                history.push(redirectLobPath);
            }).catch(() => {
                modalApi.showConfirm({
                    title: commonMessages.unableToDraftPolicy,
                    message: commonMessages.anErrorOccurred,
                    status: 'warning',
                    icon: 'gw-error-outline'
                });
            }).finally(() => {
                setIsLoading(false);
            });
            return true;
        }, _.noop);
    }, [hasErrorOccurred, modalApi, EndorsementService, authHeader, history]);

    const handleError = useCallback(
        (error) => {
            const isQuotePage = _.includes(_.toLower(_.get(error, 'gwInfo.method')), 'quote');
            const jobIDFromWizard = _.get(error, 'gwInfo.params[0].jobID');
            const jobIDFromLocation = _.get(location, 'state.policyNumber');
            let redirectPath = `/contactAgent/${jobIDFromLocation}`;
            if (!_.isEmpty(jobIDFromWizard)) {
                redirectPath = `/change/${jobIDFromWizard}/summary`;
            }
            const state = {
                pathname: redirectPath,
                state: {
                    redirectPath: '/home',
                }
            };

            return modalApi.showAlert({
                title: isQuotePage ? messages.saveQuoteError : messages.anErrorOccurredTitle,
                message: isQuotePage ? messages.saveQuoteErrorMessage : messages.anErrorOccurred,
                status: 'error',
                icon: 'gw-error-outline',
                confirmButtonText: platformMessages.ok
            }).then(() => {
                setHasErrorOccurred(true);
                return <Redirect to={state} />;
            }, _.noop);
        },
        [location, modalApi]
    );

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <div className={styles.paPolicyChangeWizardContainer}>
            <ErrorBoundary onError={handleError}>
                <Wizard
                    initialSteps={steps}
                    wizardTitle={title}
                    initialData={{
                        submissionVM: initialSubmission,
                        selectedPages: {
                            address: false,
                            vehicles: false,
                            drivers: false,
                            coverages: false
                        },
                        wizardPageData: {}
                    }}
                    onCancel={handleOnCancel}
                    onPreviousModalProps={{
                        title: platformMessages.wantToJump,
                        message: platformMessages.wantToJumpMessage,
                        status: 'warning',
                        icon: 'gw-error-outline',
                        confirmButtonText: platformMessages.yes,
                        cancelButtonText: platformMessages.no
                    }}
                    extractVMObject={(data) => _.get(data, 'submissionVM')}
                />
            </ErrorBoundary>
        </div>
    );
}

CEPAPolicyChangeWizard.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func
    }).isRequired,
    location: PropTypes.shape({
        state: PropTypes.shape({
            address: PropTypes.shape({}),
            policyNumber: PropTypes.string,
            requestType: PropTypes.string,
            policyDetails: PropTypes.shape({
               // effective: PropTypes.instanceOf(Date)
            })
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({
        Authorization: PropTypes.string
    })
};

export default withRouter(withViewModelService(CEPAPolicyChangeWizard));
